"use client";

import { IUserFull, IUserSelectable } from "@gettactic/api";
import { useUserListByIds, useUserListPaginated } from "@gettactic/hooks";
import React from "react";
import { FormControlWrapper } from "../FormControlWrapper";
import { UserSelectInput } from "./UserSelectInput";
import { AsyncUserSelectInputProps } from "./UserSelectInput.types";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

/**
 * {@link UserSelectInput} that searches the org roster **server-side** as you
 * type instead of filtering a pre-fetched page. Use this whenever the pool is
 * the whole organization: a pre-fetched list can only match the users that came
 * back in the first request, so anyone past that page stays unreachable in orgs
 * larger than the page size.
 *
 * The current selection is hydrated by id so the trigger still renders it even
 * when those users are outside the loaded pages.
 */
export const AsyncUserSelectInput = React.forwardRef<
  React.ComponentRef<typeof FormControlWrapper>,
  AsyncUserSelectInputProps
>(({ roles, value, values, ...props }, ref) => {
  const [query, setQuery] = React.useState("");
  const [debouncedQuery, setDebouncedQuery] = React.useState("");

  React.useEffect(() => {
    const timeout = setTimeout(() => setDebouncedQuery(query), DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [query]);

  const userList = useUserListPaginated(debouncedQuery, PAGE_SIZE, {}, roles);

  const selectedIds = React.useMemo(
    () => (values ?? (value ? [value] : [])).filter(Boolean),
    [value, values]
  );
  const selectedHydration = useUserListByIds(selectedIds);

  const users = React.useMemo<IUserSelectable[]>(() => {
    const byId = new Map<string, IUserSelectable>();
    for (const user of userList.allPages) byId.set(user.id, user);
    const hydrated = (selectedHydration.data ?? []).filter(
      Boolean
    ) as IUserFull[];
    for (const user of hydrated) {
      if (!byId.has(user.id)) byId.set(user.id, user);
    }
    return [...byId.values()];
  }, [userList.allPages, selectedHydration.data]);

  return (
    <UserSelectInput
      ref={ref}
      {...props}
      value={value}
      values={values}
      users={users}
      onSearchChange={setQuery}
      // `isFetching` from useInfiniteQuery is also true while a next page is
      // loading, not just while the search query itself is in flight — guard
      // it out here, or paginating would disable the already-visible items
      // and show "Searching…" for a plain scroll-to-load-more.
      isSearching={
        query !== debouncedQuery ||
        (userList.isFetching && !userList.isFetchingNextPage)
      }
      hasNextPage={userList.hasNextPage}
      fetchNextPage={userList.fetchNextPage}
      isFetchingNextPage={userList.isFetchingNextPage}
    />
  );
});

AsyncUserSelectInput.displayName = "AsyncUserSelectInput";
